import React, { useState, useMemo } from "react";
import { useProjects } from "../../context/ProjectContext";
import { useTasks } from "../../context/TaskContext";
import { EditProjectModal } from "./EditProjectModal";

export const Sidebar = ({ user, currentFilter, setFilter, onAddTaskClick, onAddProjectClick, isOpen, setIsOpen }) => {
    const { projects } = useProjects();
    const { tasks } = useTasks();

    // Estado para controlar qué proyecto se está editando
    const [editingProject, setEditingProject] = useState(null);

    // Calcular los contadores de manera eficiente usando useMemo
    const taskCounts = useMemo(() => {
        const todayStr = new Date().toISOString().split("T")[0];

        // Tareas activas (pendientes)
        const activeTasks = tasks.filter(task => !task.done);

        const inboxCount = activeTasks.filter(task => task.project_id === null).length;
        const todayCount = activeTasks.filter(task => task.due_date === todayStr).length;
        const upcomingCount = activeTasks.filter(task => task.due_date > todayStr).length;

        const projectCounts = activeTasks.reduce((acc, task) => {
            if (task.project_id !== null) {
                acc[task.project_id] = (acc[task.project_id] || 0) + 1;
            }
            return acc;
        }, {});

        return {
            inbox: inboxCount,
            today: todayCount,
            upcoming: upcomingCount,
            projects: projectCounts
        };
    }, [tasks]);

    return (
        <>
            {/* Telón oscuro translúcido EXCLUSIVO para móvil cuando el sidebar esté desplegado */}
            {isOpen && (
                <div
                    onClick={() => setIsOpen(false)}
                    className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
                />
            )}

            <aside
                className={`bg-slate-50 border-r border-slate-200 flex flex-col justify-between py-4 flex-shrink-0 transition-all duration-300 ease-in-out
                    
                    /* 💡 CLAVE 1: Deshabilitar explícitamente el scroll en el eje X y ajustar padding horizontal */
                    overflow-x-hidden ${isOpen ? "px-4" : "px-2"}
                    
                    /* POSICIONAMIENTO FIJO EN MÓVIL Y STICKY EN DESKTOP */
                    fixed top-16 bottom-0 left-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:bottom-auto lg:left-auto
                    
                    /* MÓVIL (< lg): Cuando se abre, flota sobre las tareas en z-50 */
                    ${isOpen
                        ? "z-50 shadow-2xl w-64 lg:shadow-none"
                        : "z-30 w-14 shadow-none"
                    }
                `}
            >
                <div className="space-y-6 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar">

                    {/* Cabecera del Sidebar */}
                    <div className={`flex items-center ${isOpen ? "justify-between" : "justify-center"}`}>
                        {isOpen && <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Menú</span>}
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="text-slate-500 hover:text-slate-800 p-1.5 rounded-lg hover:bg-slate-200/60 transition-colors relative group"
                        >
                            <i className={`fas ${isOpen ? "fa-columns" : "fa-bars"} text-sm`}></i>

                            {!isOpen && (
                                <span className="hidden lg:block absolute left-full ml-3 px-2 py-1 bg-slate-800 text-white text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-md">
                                    {isOpen ? "Ocultar menú" : "Mostrar menú"}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Botón Añadir Tarea */}
                    <button
                        onClick={() => {
                            onAddTaskClick();
                            if (window.innerWidth < 1024) setIsOpen(false);
                        }}
                        className={`bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-sm transition-all flex items-center justify-center text-sm transform active:scale-95 relative group
                            ${isOpen ? "w-full py-2.5 px-4 space-x-2" : "w-10 h-10 mx-auto"}`}
                    >
                        <i className="fas fa-plus-circle text-base"></i>
                        {isOpen && <span>Añadir tarea</span>}

                        {!isOpen && (
                            <span className="hidden lg:block absolute left-full ml-4 px-2.5 py-1 bg-slate-800 text-white text-xs font-semibold rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-md">
                                Añadir tarea
                            </span>
                        )}
                    </button>

                    {/* Vistas principales */}
                    <div className="space-y-1">
                        {[
                            { id: 'inbox', label: 'Bandeja de entrada', icon: 'fa-inbox', count: taskCounts.inbox },
                            { id: 'today', label: 'Hoy', icon: 'fa-calendar-day', count: taskCounts.today },
                            { id: 'upcoming', label: 'Próximo', icon: 'fa-calendar-alt', count: taskCounts.upcoming }
                        ].map((item) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setFilter(item.id);
                                    if (window.innerWidth < 1024) setIsOpen(false);
                                }}
                                className={`flex items-center rounded-lg text-sm font-medium transition-colors relative group
                                    ${currentFilter === item.id ? 'bg-orange-50 text-orange-700' : 'text-slate-700 hover:bg-slate-200/50'}
                                    ${isOpen ? 'w-full justify-between px-3 py-2' : 'w-10 h-10 justify-center mx-auto'}`}
                            >
                                <div className="flex items-center space-x-3">
                                    <i className={`fas ${item.icon} text-slate-500`}></i>
                                    {isOpen && <span>{item.label}</span>}
                                </div>

                                {isOpen && item.count > 0 && (
                                    <span className="text-xs px-1.5 py-0.5 bg-slate-200/60 text-slate-600 rounded-md font-semibold">
                                        {item.count}
                                    </span>
                                )}

                                {!isOpen && (
                                    <span className="hidden lg:block absolute left-full ml-4 px-2.5 py-1 bg-slate-800 text-white text-xs font-semibold rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-md">
                                        {item.label} {item.count > 0 ? `(${item.count})` : ''}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Sección de Mis Proyectos */}
                    <div className="space-y-2">
                        <div className={`flex items-center mb-2 ${isOpen ? "justify-between px-3" : "justify-center"}`}>
                            {isOpen ? (
                                <>
                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                        Mis Proyectos
                                    </span>
                                    <button
                                        type="button"
                                        onClick={onAddProjectClick}
                                        className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-200/50 transition-colors"
                                        title="Añadir proyecto"
                                    >
                                        <i className="fas fa-plus text-xs"></i>
                                    </button>
                                </>
                            ) : (
                                <div className="w-6 border-b border-slate-300"></div>
                            )}
                        </div>

                        <div className="space-y-1">
                            {projects.map((project) => {
                                const projectCount = taskCounts.projects[project.id] || 0;
                                return (
                                    <div key={project.id} className="relative group flex items-center">
                                        <button
                                            onClick={() => {
                                                setFilter(`project-${project.id}`);
                                                if (window.innerWidth < 1024) setIsOpen(false);
                                            }}
                                            className={`flex items-center rounded-lg text-sm text-slate-600 hover:bg-slate-200/50 transition-colors relative
                                                ${currentFilter === `project-${project.id}` ? 'bg-orange-50 text-orange-700 font-medium' : ''}
                                                ${isOpen ? 'w-full justify-between pl-3 pr-8 py-1.5' : 'w-10 h-10 justify-center mx-auto'}`}
                                        >
                                            <div className="flex items-center space-x-3 truncate">
                                                <span className="text-sm" style={{ color: project.color }}>
                                                    {project.icon}
                                                </span>
                                                {isOpen && <span className="truncate text-slate-700">{project.name}</span>}
                                            </div>

                                            {/* Contador del proyecto*/}
                                            {isOpen && projectCount > 0 && (
                                                <span className="text-xs px-1.5 py-0.5 bg-slate-200/60 text-slate-600 rounded-md font-semibold ">
                                                    {projectCount}
                                                </span>
                                            )}

                                            {!isOpen && (
                                                <span className="hidden lg:block absolute left-full ml-4 px-2.5 py-1 bg-slate-800 text-white text-xs font-semibold rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-md">
                                                    {project.name} {projectCount > 0 ? `(${projectCount})` : ''}
                                                </span>
                                            )}
                                        </button>

                                        {/* Botón de Edición */}
                                        {isOpen && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingProject(project);
                                                }}
                                                className="absolute right-2 p-1 text-slate-400 hover:text-blue-600 rounded opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                                                title="Editar proyecto"
                                            >
                                                <i className="far fa-edit text-xs"></i>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>

                {/* Avatar inferior del usuario */}
                {user && (
                    <div className={`pt-4 border-t border-slate-200 flex items-center relative group ${isOpen ? "space-x-3" : "justify-center"}`}>
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            {user.email?.charAt(0).toUpperCase()}
                        </div>
                        {isOpen && (
                            <div className="truncate flex-1">
                                <p className="text-xs font-semibold text-slate-700 truncate">{user.email?.split('@')[0]}</p>
                                <p className="text-[10px] text-slate-400 truncate">{user.email}</p>
                            </div>
                        )}

                        {!isOpen && (
                            <span className="hidden lg:block absolute left-full ml-4 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-semibold rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-md">
                                {user.email}
                            </span>
                        )}
                    </div>
                )}
            </aside>

            {/* Modal de edición de proyectos */}
            <EditProjectModal
                isOpen={Boolean(editingProject)}
                project={editingProject}
                onClose={() => setEditingProject(null)}
                onProjectDeleted={(deletedId) => {
                    if (currentFilter === `project-${deletedId}`) {
                        setFilter("inbox");
                    }
                }}
            />
        </>
    );
};